<template>
  <div v-intersection.once="onIntersection">
    <div class="row justify-between items-center q-pb-md heading">
      <div class="title">
        <slot name="title">
          <h2 class="text-h5 text-weight-medium no-margin">
            Videos
          </h2>
        </slot>
      </div>
      <div
        v-if="slideCount > 1 && screen.gt.sm"
        class="row q-gutter-x-sm navigation"
      >
        <q-btn
          :icon="mdiChevronLeft"
          text-color="primary"
          padding="3px 2px"
          style="font-size: 20px"
          flat
          dense
          @click="carousel?.slidePrev()"
        />
        <q-btn
          :icon="mdiChevronRight"
          text-color="primary"
          padding="3px 2px"
          style="font-size: 20px"
          flat
          dense
          @click="carousel?.slideNext()"
        />
      </div>
    </div>

    <div
      v-if="pending || !data"
      class="row no-wrap overflow-hidden q-col-gutter-md"
    >
      <div
        v-for="item in product.videoCount"
        :key="item"
        class="item col-12 col-sm-6 col-md-4 col-lg-3"
      >
        <q-responsive ratio="1.7777">
          <q-skeleton square />
        </q-responsive>
      </div>
    </div>

    <slot
      v-else-if="!items.length"
      name="empty"
    >
      <lazy-q-banner
        class="text-white bg-red full-width q-my-md"
        inline-actions
      >
        No videos found.
        <template #action>
          <q-btn
            flat
            square
            color="white"
            label="Retry"
            @click="refresh()"
          />
        </template>
      </lazy-q-banner>
    </slot>

    <swiper
      v-else
      :modules="[Autoplay]"
      :slides-per-view="visibleItems"
      :space-between="16"
      :autoplay="{
        delay: 2500,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }"
      :loop="items.length > visibleItems"
      auto-height
      @swiper="(swiper) => carousel = swiper"
    >
      <swiper-slide
        v-for="item in items"
        :key="item.url"
        class="cursor-pointer"
      >
        <ProductVideoItem :item />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script lang="ts" setup>
import type SwiperClass from 'swiper';
import { mdiChevronLeft, mdiChevronRight } from '@quasar/extras/mdi-v7';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay } from 'swiper/modules';
import type { PaginatedCollection } from '~/types/ecommerce';

import 'swiper/scss';

const { product } = useProduct()!;
const { $ecommerce } = useNuxtApp();
const { screen } = useQuasar();

const carousel = ref<SwiperClass | null>(null);

const { data, status, execute, refresh } = await useAsyncData(
  `products_${product.id}_videos`,
  () => $ecommerce.fetch<PaginatedCollection<{ url: string }>>(`/products/${product.id}/videos`),
  {
    lazy: true,
    server: false,
    immediate: false,
    getCachedData: getCachedPayload,
  },
);

const items = computed(() => data.value?.items ?? []);

const pending = computed(() => import.meta.server || status.value == 'pending');

const visibleItems = computed(() => {
  if (import.meta.server) {
    return 1;
  }

  switch (screen.name) {
    case 'sm':
      return 2;
    case 'md':
      return 3;
    case 'lg':
      return 4;
    case 'xl':
      return 5;
    default:
      return 1;
  }
});

const slideCount = computed(() => {
  return Math.ceil(items.value.length / visibleItems.value);
});

const onIntersection = (entry: IntersectionObserverEntry) => {
  if (!entry.isIntersecting) {
    return true;
  }

  if (data.value) {
    return false;
  }

  execute();
  return false;
};
</script>
